import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const FAQPage = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const faqs = [
    {
      question: 'What services do you offer?',
      answer: 'I provide full-stack development services including web application development, website design, custom program development, UI/UX design, and consulting. Whether you need a simple website or a complex application, I can help bring your ideas to life.'
    },
    {
      question: 'What technologies do you work with?',
      answer: 'I specialize in modern web technologies including React, Node.js, TypeScript, Firebase, Tailwind CSS, Three.js, and more. I stay current with industry trends and use the best tools for each project.'
    },
    {
      question: 'How much does a project cost?',
      answer: 'Project costs vary based on scope, complexity, and timeline. I provide custom quotes after understanding your requirements. Contact me for a free consultation to discuss your project and get an estimate.'
    },
    {
      question: 'What is your typical project timeline?',
      answer: 'Timeline depends on project scope. Small websites typically take 2-4 weeks, while complex applications may take 2-3 months or longer. We discuss timelines upfront and keep you updated throughout development.'
    },
    {
      question: 'Do you work with startups?',
      answer: 'Yes! I love working with startups and ambitious teams. I understand the unique challenges of early-stage companies and can help you build scalable, cost-effective solutions.'
    },
    {
      question: 'Can you work on existing projects?',
      answer: 'Absolutely. I can help with bug fixes, feature additions, performance optimization, and refactoring of existing applications. Contact me with details about your project.'
    },
    {
      question: 'What is your development process?',
      answer: 'My process includes: discovery & planning, design, development with regular updates, testing & QA, deployment, and ongoing support. I prioritize clear communication and collaboration throughout.'
    },
    {
      question: 'Do you provide hosting and maintenance?',
      answer: 'I can recommend hosting solutions and assist with deployment. For ongoing maintenance and support, we can discuss options based on your needs. Most of my projects are hosted on modern platforms like Firebase, Vercel, or AWS.'
    },
    {
      question: 'How do you handle security?',
      answer: 'Security is paramount. I implement industry-standard practices including SSL/TLS encryption, secure authentication, data protection, and regular security updates. All projects meet GDPR and data privacy requirements.'
    },
    {
      question: 'What is your response time?',
      answer: 'I typically respond to inquiries within 24 hours. During active projects, I provide regular updates and maintain open communication. Support clients receive priority response times.'
    },
    {
      question: 'Can you help with project consultation?',
      answer: 'Yes. I offer free initial consultations to discuss your project goals, technical requirements, timeline, and budget. This helps us determine if we\'re a good fit and creates a roadmap for success.'
    },
    {
      question: 'How do you handle payments?',
      answer: 'Payment terms are discussed in the project contract and typically follow a milestone-based schedule. I accept various payment methods for flexibility. A contract outlines all financial details clearly.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <main className="pt-20 px-4 sm:px-6 lg:px-8 pb-24">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-2 mb-12">
            <h1 className="text-5xl font-bold text-slate-900">Frequently Asked Questions</h1>
            <p className="text-lg text-slate-600">Find answers to common questions about my services, process, and how we can work together.</p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-slate-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition"
                >
                  <h3 className="text-lg font-semibold text-slate-900 text-left">{faq.question}</h3>
                  <ChevronDown
                    size={20}
                    className={`text-slate-600 flex-shrink-0 transition-transform ${
                      expandedIndex === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>

                {expandedIndex === index && (
                  <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                    <p className="text-slate-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Didn't find your answer?</h3>
            <p className="text-slate-700 mb-4">
              If you have a question that's not covered here, feel free to reach out. I'm happy to help!
            </p>
            <a
              href="/contact"
              className="inline-block px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
            >
              Contact Me
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FAQPage;
